import React, { useEffect, useState } from "react";
import { FaBan, FaHistory, FaPlus, FaSearch, FaTrash } from "react-icons/fa";
import CancelBookingModal from "../components/CancelBookingModal";
import ConfirmDialog from "../components/ConfirmDialog";
import { useHiddenMode } from "../contexts/HiddenModeContext";
import "../css/pages/BookingDetails.css";
import { usePageTitle } from "../hooks/usePageTitle";
import bookingService, { Booking } from "../services/bookingService";
import { Income, incomeService } from "../services/incomeService";
import { Project, projectService } from "../services/projectService";
import {
  formatCurrency,
  formatDate,
  formatHiddenIncome,
} from "../utils/formatters";
import { formatMainDBAmount, formatSystemsDBAmount, formatTotalAmount } from "../utils/formatAmount";

const BookingDetails: React.FC = () => {
  usePageTitle();
  const { isHiddenModeActive } = useHiddenMode();

  const [searchTerm, setSearchTerm] = useState("");
  const [searchResults, setSearchResults] = useState<Booking[]>([]);
  const [selectedBooking, setSelectedBooking] = useState<Booking | null>(null);
  const [paymentHistory, setPaymentHistory] = useState<Income[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showAddPayment, setShowAddPayment] = useState(false);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);

  // Payment form state
  const [paymentForm, setPaymentForm] = useState({
    amount: "",
    paymentDate: new Date().toISOString().split("T")[0],
    paymentMethod: "cheque" as const,
    referenceNumber: "",
    bankName: "",
    description: "",
  });

  const [confirmDialog, setConfirmDialog] = useState<{
    isOpen: boolean;
    title: string;
    message: string;
    onConfirm: () => void;
  }>({
    isOpen: false,
    title: "",
    message: "",
    onConfirm: () => {},
  });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const projectsData = await projectService.getProjects();
      setProjects(projectsData);
    } catch (err) {
      console.warn("Failed to load projects:", err);
      // Don't set error here since projects are not critical for basic functionality
    }
  };

  const handleSearch = async () => {
    if (!searchTerm.trim()) {
      setError("Please enter a search term");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const result = await bookingService.getBookings({
        search: searchTerm,
        status: "active",
        page: 1,
        limit: 10,
      });

      setSearchResults(result.bookings);

      if (result.bookings.length === 0) {
        setError("No bookings found for the search term");
      }
    } catch (err) {
      setError("Failed to search bookings");
    } finally {
      setLoading(false);
    }
  };

  const handleSelectBooking = async (booking: Booking) => {
    setSelectedBooking(booking);
    setSearchResults([]);
    setShowAddPayment(false);

    // Load payment history
    try {
      const payments = await incomeService.getIncomesByBooking(booking.id);
      setPaymentHistory(payments);
    } catch (err) {
      setError("Failed to load payment history");
      setPaymentHistory([]);
    }
  };

  const handleAddPayment = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!selectedBooking) return;

    try {
      await incomeService.createIncome({
        bookingId: selectedBooking.id,
        amount: parseFloat(paymentForm.amount),
        paymentDate: paymentForm.paymentDate,
        paymentMethod: paymentForm.paymentMethod,
        referenceNumber: paymentForm.referenceNumber || undefined,
        bankName: paymentForm.bankName || undefined,
        description: paymentForm.description || undefined,
      });

      // Reload payment history
      const payments = await incomeService.getIncomesByBooking(
        selectedBooking.id
      );
      setPaymentHistory(payments);

      // Reset form
      setPaymentForm({
        amount: "",
        paymentDate: new Date().toISOString().split("T")[0],
        paymentMethod: "cheque",
        referenceNumber: "",
        bankName: "",
        description: "",
      });

      setShowAddPayment(false);
      setError("");
    } catch (err) {
      setError("Failed to add payment");
    }
  };

  const handleDeletePayment = async (paymentId: number) => {
    try {
      await incomeService.deleteIncome(paymentId);

      // Reload payment history
      if (selectedBooking) {
        const payments = await incomeService.getIncomesByBooking(
          selectedBooking.id
        );
        setPaymentHistory(payments);
      }
    } catch (err) {
      setError("Failed to delete payment");
    }
  };

  const confirmDeletePayment = (payment: Income) => {
    setConfirmDialog({
      isOpen: true,
      title: "Delete Payment",
      message: `Are you sure you want to delete this payment of ${formatCurrency(
        payment.amount
      )}?`,
      onConfirm: () => {
        handleDeletePayment(payment.id);
        setConfirmDialog((prev) => ({ ...prev, isOpen: false }));
      },
    });
  };

  const handleCancelBooking = async (cancellationData: any) => {
    if (!selectedBooking) return;

    setCancelLoading(true);
    try {
      await bookingService.cancelBooking(selectedBooking.id, cancellationData);

      // Reset state and close modal
      setSelectedBooking(null);
      setPaymentHistory([]);
      setShowCancelModal(false);
      setSearchResults([]);
      setError("");

      // Show success message
      alert("Booking cancelled successfully");
    } catch (err) {
      console.error("Cancellation failed:", err);
      setError("Failed to cancel booking. Please try again.");
    } finally {
      setCancelLoading(false);
    }
  };

  const getCustomerName = (booking: Booking) => {
    const primaryApplicant = booking.applicants.find((a) => a.isPrimary);
    return primaryApplicant
      ? `${primaryApplicant.firstName} ${primaryApplicant.surname}`
      : "N/A";
  };

  const getPaymentMethodLabel = (method: string) => {
    const methods: Record<string, string> = {
      cheque: "Cheque",
      neft: "NEFT",
      imps: "IMPS",
      rtgs: "RTGS",
    };
    return methods[method] || method.toUpperCase();
  };

  const getStatusBadge = (status: string) => {
    const statusClasses: Record<string, string> = {
      approval_pending: "status-pending",
      approved: "status-approved",
      rejected: "status-rejected",
    };

    const statusLabels: Record<string, string> = {
      approval_pending: "Pending",
      approved: "Approved",
      rejected: "Rejected",
    };

    return (
      <span className={`status-badge ${statusClasses[status] || ""}`}>
        {statusLabels[status] || status}
      </span>
    );
  };

  const totalPaidAmount = paymentHistory
    .filter((payment) => payment.status === "approved")
    .reduce((sum, payment) => sum + payment.amount, 0);

  const pendingAmount = paymentHistory
    .filter((payment) => payment.status === "approval_pending")
    .reduce((sum, payment) => sum + payment.amount, 0);

  const remainingAmount = selectedBooking
    ? selectedBooking.totalAmount - totalPaidAmount
    : 0;

  return (
    <div className="page-container">
      <div className="page-header">
        <div style={{ color: "#1a1a1a" }}>
          <h1 style={{ color: "#1a1a1a", margin: "0 0 4px 0" }}>
            Booking Details & Payments
          </h1>
          <p style={{ color: "#666", margin: 0 }}>
            Search for a booking to view details and manage payments
          </p>
        </div>
      </div>

      {/* Search Section */}
      <div className="search-section">
        <div className="search-input-container">
          <FaSearch className="search-icon" />
          <input
            type="text"
            placeholder="Search by customer name, unit number, or project name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            onKeyPress={(e) => e.key === "Enter" && handleSearch()}
            className="form-input"
          />
          <button
            onClick={handleSearch}
            disabled={loading}
            className="search-btn"
          >
            <FaSearch /> {loading ? "Searching..." : "Search"}
          </button>
        </div>

        {error && <div className="error-message">{error}</div>}

        {/* Search Results */}
        {searchResults.length > 0 && (
          <div className="search-results">
            <h3>Search Results</h3>
            <div className="results-list">
              {searchResults.map((booking) => (
                <div
                  key={booking.id}
                  className="result-item"
                  onClick={() => handleSelectBooking(booking)}
                >
                  <div className="result-main">
                    <span className="customer-name">
                      {getCustomerName(booking)}
                    </span>
                    <span className="booking-id">#{booking.bookingId}</span>
                  </div>
                  <div className="result-details">
                    <span>
                      {booking.unit?.project?.name ||
                        booking.project?.name ||
                        "N/A"}
                    </span>
                    <span>
                      {booking.unit.wing}-{booking.unit.unitNo}
                    </span>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        fontSize: "11px",
                      }}
                    >
                      <span>
                        Main: {formatMainDBAmount(booking.saleDeedAmount || 0)}
                      </span>
                      {isHiddenModeActive && (
                        <span>
                          Receivables:{" "}
                          {formatSystemsDBAmount(
                            (booking.chargesAmount || 0) +
                              (booking.terraceAmount || 0) +
                              (booking.otherWorkAmount || 0)
                          )}
                        </span>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* Selected Booking Details */}
      {selectedBooking && (
        <div className="booking-details">
          <div className="details-header">
            <h2>Booking Details</h2>
            <div className="header-actions">
              <button
                onClick={() => setShowAddPayment(!showAddPayment)}
                className="add-payment-btn"
              >
                <FaPlus /> Add Payment
              </button>
              <button
                onClick={() => setShowCancelModal(true)}
                className="cancel-booking-btn"
                title="Cancel Booking"
              >
                <FaBan /> Cancel Booking
              </button>
            </div>
          </div>

          <div className="details-grid">
            <div className="booking-info">
              <h3>Booking Information</h3>
              <div className="info-row">
                <span className="label">Booking ID:</span>
                <span className="value">#{selectedBooking.bookingId}</span>
              </div>
              <div className="info-row">
                <span className="label">Customer:</span>
                <span className="value">
                  {getCustomerName(selectedBooking)}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Project:</span>
                <span className="value">
                  {selectedBooking.unit?.project?.name ||
                    selectedBooking.project?.name ||
                    "N/A"}
                </span>
              </div>
              <div className="info-row">
                <span className="label">Unit:</span>
                <span className="value">
                  {selectedBooking.unit.wing}-{selectedBooking.unit.unitNo}(
                  {selectedBooking.unit.unitType})
                </span>
              </div>
              <div className="info-row">
                <span className="label">Booking Date:</span>
                <span className="value">
                  {new Date(selectedBooking.bookingDate).toLocaleDateString()}
                </span>
              </div>
            </div>

            <div className="payment-summary">
              <h3>Payment Summary</h3>
              <div className="summary-row">
                <span className="label">Main Amount (Sale Deed):</span>
                <span className="value">
                  {formatMainDBAmount(selectedBooking.saleDeedAmount || 0)}
                </span>
              </div>
              {isHiddenModeActive && (
                <>
                  <div className="summary-row">
                    <span className="label">Receivables Amount (Charges):</span>
                    <span className="value">
                      {formatSystemsDBAmount(selectedBooking.chargesAmount || 0)}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="label">Receivables Amount (Terrace):</span>
                    <span className="value">
                      {formatSystemsDBAmount(selectedBooking.terraceAmount || 0)}
                    </span>
                  </div>
                  <div className="summary-row">
                    <span className="label">
                      Receivables Amount (Other Work):
                    </span>
                    <span className="value">
                      {formatSystemsDBAmount(selectedBooking.otherWorkAmount || 0)}
                    </span>
                  </div>
                </>
              )}
              <div className="summary-row total">
                <span className="label">Total Amount:</span>
                <span className="value">
                  {formatTotalAmount(selectedBooking.totalAmount)}
                </span>
              </div>
              <div className="summary-row paid">
                <span className="label">Paid Amount:</span>
                <span className="value">{formatTotalAmount(totalPaidAmount)}</span>
              </div>
              <div className="summary-row pending">
                <span className="label">Pending Approval:</span>
                <span className="value">{formatTotalAmount(pendingAmount)}</span>
              </div>
              <div className="summary-row remaining">
                <span className="label">Remaining Amount:</span>
                <span className="value">{formatTotalAmount(remainingAmount)}</span>
              </div>
            </div>
          </div>

          {/* Add Payment Form */}
          {showAddPayment && (
            <div className="add-payment-section">
              <h3>Add New Payment</h3>
              <form onSubmit={handleAddPayment} className="payment-form">
                <div className="form-grid">
                  <div className="form-group">
                    <label>Amount *</label>
                    <input
                      type="number"
                      step="0.01"
                      value={paymentForm.amount}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          amount: e.target.value,
                        }))
                      }
                      onWheel={(e) => e.currentTarget.blur()}
                      placeholder="Enter amount"
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Date *</label>
                    <input
                      type="date"
                      value={paymentForm.paymentDate}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          paymentDate: e.target.value,
                        }))
                      }
                      max={new Date().toISOString().split('T')[0]}
                      required
                    />
                  </div>

                  <div className="form-group">
                    <label>Payment Method *</label>
                    <select
                      value={paymentForm.paymentMethod}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          paymentMethod: e.target.value as any,
                        }))
                      }
                      required
                    >
                      <option value="cheque">Cheque</option>
                      <option value="rtgs">RTGS</option>
                      <option value="neft">NEFT</option>
                      <option value="imps">IMPS</option>
                      <option value="upi">UPI</option>
                      <option value="mb">MB</option>
                      <option value="transfer">Transfer</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label>Reference Number</label>
                    <input
                      type="text"
                      value={paymentForm.referenceNumber}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          referenceNumber: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="form-group">
                    <label>Bank Name</label>
                    <input
                      type="text"
                      value={paymentForm.bankName}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          bankName: e.target.value,
                        }))
                      }
                    />
                  </div>

                  <div className="form-group full-width">
                    <label>Description</label>
                    <textarea
                      value={paymentForm.description}
                      onChange={(e) =>
                        setPaymentForm((prev) => ({
                          ...prev,
                          description: e.target.value,
                        }))
                      }
                      rows={3}
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <button type="submit" className="submit-btn">
                    <FaPlus /> Add Payment
                  </button>
                  <button
                    type="button"
                    onClick={() => setShowAddPayment(false)}
                    className="cancel-btn"
                  >
                    Cancel
                  </button>
                </div>
              </form>
            </div>
          )}

          {/* Payment History */}
          <div className="payment-history">
            <div className="history-header">
              <h3>
                <FaHistory /> Payment History
              </h3>
              <span className="payment-count">
                {paymentHistory.length} payment
                {paymentHistory.length !== 1 ? "s" : ""}
              </span>
            </div>

            {paymentHistory.length === 0 ? (
              <div className="no-payments">
                <p>No payments found for this booking.</p>
              </div>
            ) : (
              <div className="content-section">
                <table className="standard-table">
                  <thead>
                    <tr>
                      <th>Sr. No</th>
                      <th>Date</th>
                      <th>Amount</th>
                      <th>Method</th>
                      <th>Reference</th>
                      <th>Bank</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentHistory.map((payment, index) => (
                      <tr key={payment.id}>
                        <td style={{ fontWeight: "600", color: "#4f46e5" }}>
                          {paymentHistory.length - index}
                        </td>
                        <td>{formatDate(payment.paymentDate)}</td>
                        <td style={{color: payment.amount < 0 ? '#dc2626' : '#059669'}}>
                          {formatTotalAmount(payment.amount)}
                        </td>
                        <td>{getPaymentMethodLabel(payment.paymentMethod)}</td>
                        <td>{payment.referenceNumber || "-"}</td>
                        <td>{payment.bankName || "-"}</td>
                        <td>{getStatusBadge(payment.status)}</td>
                        <td>
                          <div className="action-buttons">
                            {payment.status === "approval_pending" && (
                              <button
                                onClick={() => confirmDeletePayment(payment)}
                                className="delete-btn"
                                title="Delete Payment"
                              >
                                <FaTrash />
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onCancel={() =>
          setConfirmDialog((prev) => ({ ...prev, isOpen: false }))
        }
      />

      {selectedBooking && (
        <CancelBookingModal
          isOpen={showCancelModal}
          onClose={() => setShowCancelModal(false)}
          onCancel={handleCancelBooking}
          booking={{
            id: selectedBooking.id,
            bookingId: selectedBooking.bookingId,
            customerName: getCustomerName(selectedBooking),
            unit: selectedBooking.unit.wing
              ? `${selectedBooking.unit.wing}-${selectedBooking.unit.unitNo}`
              : selectedBooking.unit.unitNo,
            project:
              selectedBooking.unit?.project?.name ||
              selectedBooking.project?.name ||
              "N/A",
            totalReceived: totalPaidAmount,
          }}
          loading={cancelLoading}
        />
      )}
    </div>
  );
};

export default BookingDetails;
